import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  icon: string;
  items: FAQItem[];
}

const faqData: FAQCategory[] = [
  {
    title: "Getting Started",
    icon: "ri-rocket-line",
    items: [
      {
        question: "How do I begin home care services with Sycamore Oasis?",
        answer:
          "Getting started is simple. Call us at 773-699-7894 or fill out our contact form. We will schedule a complimentary in-home assessment within 24 to 48 hours. During the visit, a care coordinator will learn about your loved one's needs, daily routines, preferences, and overall situation. Within two business days, we present a personalized care plan and introduce a matched caregiver.",
      },
      {
        question: "Is the initial assessment really free?",
        answer:
          "Yes, the initial in-home assessment is completely free and carries no obligation. It is our opportunity to understand your situation and your chance to meet our team, ask questions, and see if Sycamore Oasis is the right fit for your family.",
      },
      {
        question: "How quickly can care begin after I contact you?",
        answer:
          "In many cases, care can begin within 48 hours of the initial assessment. For urgent or same-day needs, we will do everything possible to arrange immediate support while still ensuring the right caregiver match.",
      },
    ],
  },
  {
    title: "Our Caregivers",
    icon: "ri-user-heart-line",
    items: [
      {
        question: "Are your caregivers employees or independent contractors?",
        answer:
          "All Sycamore Oasis caregivers are W-2 employees, not contractors. This means they receive ongoing training, workers' compensation coverage, payroll taxes, and supervision. It also means your family is protected by our full insurance and bonding, and we maintain direct quality control over every visit.",
      },
      {
        question: "What screening and training do caregivers receive?",
        answer:
          "Every caregiver undergoes a rigorous multi-step screening process: nationwide criminal background check, DMV record review, professional reference verification, and a comprehensive skills assessment. Once hired, they complete orientation in client safety, fall prevention, dementia care awareness, and communication skills. Ongoing in-service training keeps their skills current.",
      },
      {
        question: "Can I meet the caregiver before care begins?",
        answer:
          "Absolutely. We encourage a meet-and-greet between your family and the assigned caregiver before the first official shift. This introduction helps build comfort, establish rapport, and confirm that personalities and expectations align.",
      },
      {
        question: "What happens if my regular caregiver is unavailable?",
        answer:
          "Life happens. If your primary caregiver is sick or needs time off, we arrange a qualified backup caregiver who is briefed on your care plan in advance. Our care coordinators handle the transition so there is never a gap in service.",
      },
    ],
  },
  {
    title: "Services & Coverage",
    icon: "ri-service-line",
    items: [
      {
        question: "What services does Sycamore Oasis provide?",
        answer:
          "We offer a full spectrum of non-medical home care services including personal care (bathing, dressing, grooming, toileting), companionship and social engagement, meal preparation and feeding assistance, medication reminders, transportation to appointments and errands, light housekeeping, mobility assistance, and respite care for family caregivers. We also offer specialized support for dementia and Parkinson's.",
      },
      {
        question: "Do you provide live-in or overnight care?",
        answer:
          "Yes. We offer hourly care, overnight shifts, and live-in arrangements depending on your needs. Live-in caregivers reside in the home and provide around-the-clock availability with scheduled rest periods. We can design a schedule that matches your family's routine.",
      },
      {
        question: "Which counties do you serve?",
        answer:
          "We proudly serve DuPage, Will, Kane, Cook, Kendall, and LaSalle counties in Illinois. Our office is headquartered in Naperville, which allows us to maintain prompt response times and local expertise across the entire region.",
      },
      {
        question: "Do you care for clients with Alzheimer's or dementia?",
        answer:
          "Yes, memory care support is one of our core specialties. Our caregivers receive specialized training in communication techniques, behavioral support, safety protocols, and routine-building for clients with Alzheimer's, vascular dementia, Lewy body dementia, and other cognitive conditions.",
      },
    ],
  },
  {
    title: "Care Plans & Scheduling",
    icon: "ri-calendar-check-line",
    items: [
      {
        question: "How are care plans created?",
        answer:
          "Care plans are built collaboratively. After the initial assessment, our care coordinator drafts a personalized plan that includes services, schedule, caregiver qualifications, goals, and special instructions. Families review and approve the plan before care begins, and we revisit it regularly to keep it aligned with changing needs.",
      },
      {
        question: "Can I change the schedule or services later?",
        answer:
          "Of course. Care needs evolve, and your plan should evolve with them. You can adjust hours, add or remove services, or request a different caregiver at any time by contacting your care coordinator. Most changes can be implemented within 24 hours.",
      },
      {
        question: "Is there a minimum number of hours per week?",
        answer:
          "We are flexible. While many families start with a few hours per day or several visits per week, we can accommodate anything from a single four-hour visit to full-time live-in care. We work with your schedule and budget to find the right balance.",
      },
    ],
  },
  {
    title: "Pricing & Contracts",
    icon: "ri-hand-coin-line",
    items: [
      {
        question: "Are there long-term contracts or commitments?",
        answer:
          "No. We believe families should stay with us because they are satisfied, not because they are locked in. Our care plans operate on a simple agreement that you can pause, modify, or discontinue at any time with reasonable notice.",
      },
      {
        question: "How is pricing determined?",
        answer:
          "Pricing is based on the type of care, number of hours, and level of support needed. After the free assessment, we provide a clear, itemized quote with no hidden fees. Our care coordinator will walk through every line so you understand exactly what you are paying for.",
      },
      {
        question: "Do you accept long-term care insurance or veterans benefits?",
        answer:
          "We work with most major long-term care insurance providers and can assist with the necessary documentation. We also help families explore veterans' Aid and Attendance benefits and other programs that may offset care costs. Contact us to discuss your specific situation.",
      },
    ],
  },
  {
    title: "Safety & Quality",
    icon: "ri-shield-check-line",
    items: [
      {
        question: "How do you ensure client safety?",
        answer:
          "Safety is embedded in every layer of our service. Caregivers are trained in fall prevention, emergency response, and proper body mechanics. We perform regular supervisory visits, conduct phone check-ins, and use a digital care log so families can review daily activities. All caregivers are insured and bonded.",
      },
      {
        question: "What if there is an emergency during a shift?",
        answer:
          "Caregivers are trained to remain calm, assess the situation, and follow the emergency protocol outlined in the care plan. This includes contacting emergency services when needed, notifying the family immediately, and alerting our 24-hour on-call care coordinator. We maintain detailed incident documentation and conduct follow-up reviews.",
      },
      {
        question: "How do families stay informed about their loved one's care?",
        answer:
          "Families receive regular updates from the care coordinator. For Total Care Plan clients, we provide a dedicated family portal where you can view daily care notes, caregiver arrival and departure times, mood observations, and any concerns. You are always welcome to call or email us directly.",
      },
    ],
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 400);
      }
    }
  }, []);

  const toggleFAQ = (key: string) => {
    setOpenIndex(openIndex === key ? null : key);
  };

  const filteredData = searchQuery.trim()
    ? faqData
        .map((cat) => ({
          ...cat,
          items: cat.items.filter(
            (item) =>
              item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
              item.answer.toLowerCase().includes(searchQuery.toLowerCase())
          ),
        }))
        .filter((cat) => cat.items.length > 0)
    : faqData;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section
        ref={heroRef}
        className="relative py-16 md:py-24 bg-brand-green-800"
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-green-800 via-brand-green-800 to-brand-green-900" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-brand-green-700/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-green-600/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div
            className={`transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 mb-4">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-question-answer-line text-sm text-brand-green-300" />
              </div>
              <span className="text-xs font-medium text-white/90 uppercase tracking-wider">
                Questions & Answers
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Frequently Asked Questions
            </h1>
            <p className="text-sm md:text-base text-brand-green-200 max-w-2xl mx-auto leading-relaxed">
              Everything you need to know about our non-medical home care services, caregivers, scheduling, and more. If you cannot find your answer, we are always happy to talk.
            </p>
          </div>

          {/* Search */}
          <div
            className={`mt-8 max-w-xl mx-auto transition-all duration-700 delay-150 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search questions..."
                className="w-full pl-11 pr-4 py-3 text-sm rounded-full bg-white/95 backdrop-blur-sm border border-white/20 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-200 outline-none transition-all shadow-lg"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>
          </div>

          {/* Category Jump Links */}
          <div
            className={`mt-6 flex flex-wrap items-center justify-center gap-2 max-w-3xl mx-auto transition-all duration-700 delay-300 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            {faqData.map((cat) => {
              const id = cat.title.toLowerCase().replace(/\s+/g, "-");
              return (
                <button
                  key={cat.title}
                  onClick={() => {
                    const el = document.getElementById(id);
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }}
                  className="px-4 py-2 text-xs font-medium bg-white/10 hover:bg-white/20 text-white/90 rounded-full border border-white/15 backdrop-blur-sm transition-all"
                >
                  {cat.title}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12 md:py-20 bg-soft-gray">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto">
          {filteredData.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-14 h-14 rounded-full bg-brand-green-50 flex items-center justify-center mx-auto mb-4">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-search-line text-xl text-brand-green-400" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No matching questions found
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Try different keywords or reach out to us directly.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-green-600 text-white text-sm font-semibold rounded-full hover:bg-brand-green-700 transition-colors"
              >
                Contact Us
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          ) : (
            <div className="space-y-10 md:space-y-14">
              {filteredData.map((category, catIdx) => (
                <div
                  key={category.title}
                  id={category.title.toLowerCase().replace(/\s+/g, "-")}
                  className={`transition-all duration-700 ${
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                  }`}
                  style={{ transitionDelay: `${catIdx * 100 + 200}ms` }}
                >
                  {/* Category Header */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-brand-green-100 flex items-center justify-center shrink-0">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${category.icon} text-lg text-brand-green-600`} />
                      </div>
                    </div>
                    <h2 className="text-lg md:text-xl font-bold text-gray-900">
                      {category.title}
                    </h2>
                  </div>

                  {/* Accordion Items */}
                  <div className="space-y-3">
                    {category.items.map((item, itemIdx) => {
                      const key = `${catIdx}-${itemIdx}`;
                      const isOpen = openIndex === key;
                      return (
                        <div
                          key={key}
                          className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-brand-green-200 transition-colors"
                        >
                          <button
                            onClick={() => toggleFAQ(key)}
                            className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left"
                          >
                            <span className="text-sm md:text-base font-medium text-gray-900 leading-snug">
                              {item.question}
                            </span>
                            <div
                              className={`w-8 h-8 rounded-full bg-brand-green-50 flex items-center justify-center shrink-0 mt-0.5 transition-all duration-200 ${
                                isOpen ? "bg-brand-green-600" : ""
                              }`}
                            >
                              <div className="w-4 h-4 flex items-center justify-center">
                                <i
                                  className={`ri-add-line text-sm transition-all duration-200 ${
                                    isOpen
                                      ? "ri-subtract-line text-white"
                                      : "text-brand-green-600"
                                  }`}
                                />
                              </div>
                            </div>
                          </button>
                          <div
                            className={`grid transition-all duration-300 ease-out ${
                              isOpen
                                ? "grid-rows-[1fr] opacity-100"
                                : "grid-rows-[0fr] opacity-0"
                            }`}
                          >
                            <div className="overflow-hidden">
                              <div className="px-5 pb-4 pt-0">
                                <div className="border-t border-gray-100 pt-4">
                                  <p className="text-sm text-gray-600 leading-relaxed">
                                    {item.answer}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className="w-14 h-14 rounded-full bg-brand-green-50 flex items-center justify-center mx-auto mb-5">
            <div className="w-7 h-7 flex items-center justify-center">
              <i className="ri-chat-smile-3-line text-2xl text-brand-green-600" />
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Still Have Questions?
          </h2>
          <p className="text-sm md:text-base text-gray-500 max-w-xl mx-auto mb-8 leading-relaxed">
            Our care advisors are here to help. Reach out anytime for a personal conversation about your family's needs.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 py-3.5 bg-brand-green-600 text-white font-semibold rounded-full hover:bg-brand-green-700 transition-all duration-200 text-center whitespace-nowrap"
            >
              Get Free Assessment
            </Link>
            <a
              href="tel:7736997894"
              className="w-full sm:w-auto px-8 py-3.5 border border-brand-green-600 text-brand-green-700 font-semibold rounded-full hover:bg-brand-green-50 transition-all duration-200 text-center whitespace-nowrap"
            >
              Call 773-699-7894
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}